import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import IdleTimer from 'react-idle-timer'
import { getHealthSystem } from '../../../redux/actions/healthSystem'
import { generatePatientAssessment, getItems, getPatientAssessment } from '../../../redux/actions/assessment'
import { getConditions, getEncounter, getMedications, getObservations, getPatient, getProcedures, startEncounterOver, resetPatient } from '../../../redux/actions/patient'
import Assessment from '../Assessment'
import CarePlan from '../CarePlan'
import WrapUp from '../WrapUp'
import Layout from '../../Layouts/Layout'
import ErrorBoundary from '../../ErrorBoundary'
import encounterStatusEnum from '../../../utils/encounterStatusEnum'
import { AGLProvider } from '../../../contexts/AglContext'
import Loader from '../../Loader'
import ErrorMessage from '../../ErrorMessage'
import SessionAlert from '../../SessionAlert'

const Home = () => {
  const [showEncounterAlert, setShowEncounterAlert] = useState(false)
  const [sessionStatus, setSessionStatus] = useState(null)

  const history = useHistory()
  const dispatch = useDispatch()

  const handleOnIdle = () => {
    history.push('/unauth')
  }

  const activeKey = useSelector((state) => state.header.activeKey)
  const { accessTokenRecieved } = useSelector((state) => ({
    accessTokenRecieved: state.auth.accessTokenRecieved
  }))

  useEffect(() => {
    if (accessTokenRecieved) {
      dispatch(getHealthSystem())
    }
  }, [accessTokenRecieved])

  const { getHealthSystemState } = useSelector((state) => ({
    getHealthSystemState: state.healthSystem.getHealthSystem
  }))

  useEffect(() => {
    if (getHealthSystemState.success) {
      dispatch(getPatient())
    }
  }, [getHealthSystemState.success])

  const patientStatus = useSelector((state) => state.patient.userFriendlyStatus)
  const { patient, getPatientState } = useSelector((state) => ({
    patient: state.patient.patient,
    getPatientState: state.patient.getPatient
  }))

  const reset = useSelector((state) => state.patient.reset)

  useEffect(() => {
    if (getPatientState.success || reset.success) {
      dispatch(getEncounter())
    }
  }, [getPatientState.success, reset.success])

  const { getEncounterState, encounter } = useSelector((state) => ({
    getEncounterState: state.patient.getEncounter,
    encounter: state.patient.encounter
  }))

  useEffect(() => {
    if (getEncounterState.success && encounter?.status?.status) {
      const { status } = encounter
      const encounterStatus = encounterStatusEnum[status.status]

      if (encounterStatus.value === 'CREATED') {
        dispatch(getConditions())
      }

      if (encounterStatus.previousSession) {
        setShowEncounterAlert(true)
      }

      setSessionStatus(encounterStatus)
    }
  }, [getEncounterState.success, encounter?.status?.status])

  const startOver = useSelector((state) => state.patient.startEncounterOver)

  useEffect(() => {
    if (startOver.success) {
      dispatch(getConditions())
    }
  }, [startOver.success])

  const getConditionsState = useSelector((state) => state.patient.getConditions)

  useEffect(() => {
    if (getConditionsState.success) {
      dispatch(getProcedures())
    }
  }, [getConditionsState.success])

  const getProceduresState = useSelector((state) => state.patient.getProcedures)

  useEffect(() => {
    if (getProceduresState.success) {
      dispatch(getObservations())
    }
  }, [getProceduresState.success])

  const getObservationsState = useSelector((state) => state.patient.getObservations)

  useEffect(() => {
    if (getObservationsState.success) {
      dispatch(getItems())
    }
  }, [getObservationsState.success])

  const getItemsState = useSelector((state) => state.assessment.getItems)

  useEffect(() => {
    if (getItemsState.success) {
      if (showEncounterAlert) {
        setShowEncounterAlert(false)
        dispatch(getPatientAssessment())
      } else {
        dispatch(generatePatientAssessment())
      }
    }
  }, [getItemsState.success])

  const { assessmentIsFetching, assessmentIsSuccess } = useSelector((state) => ({
    assessmentIsFetching: state.assessment.isFetching,
    assessmentIsSuccess: state.assessment.isSuccess,
  }))

  useEffect(() => {
    if (assessmentIsSuccess) {
      dispatch(getMedications())
    }
  }, [assessmentIsSuccess])

  const renderStep = () => {
    switch (activeKey) {
    case 'assessment':
      return <Assessment />
    case 'ccp':
      return <CarePlan />
    case 'wrapup':
      return <WrapUp />
    default:
      break
    }
  }

  const handleExitClick = () => {
    history.push('/unauth')
  }

  const handleRetryClick = () => {
    if (getPatientState.error !== false) {
      dispatch(getPatient())
    }

    if (getEncounterState.error !== false) {
      dispatch(getEncounter())
    }

    if (getConditionsState.error !== false) {
      dispatch(getConditions())
    }

    if (getProceduresState.error !== false) {
      dispatch(getProcedures())
    }

    if (getObservationsState.error !== false) {
      dispatch(getObservations())
    }
  }

  const handleStartOverClick = () => {
    setShowEncounterAlert(false)
    dispatch(startEncounterOver())
  }

  const handleResetPatient = () => {
    setShowEncounterAlert(false)
    dispatch(resetPatient())
  }

  const handleContinueClick = () => {
    dispatch(getItems())
  }

  const handleViewSession = () => {
    setShowEncounterAlert(false)
    history.push('/close/done')
  }

  const renderErrorMessage = () => {
    const errorStates = [
      { state: getPatientState, message: 'patient' },
      { state: getEncounterState, message: 'encounter' },
      { state: getConditionsState, message: 'conditions' },
      { state: getProceduresState, message: 'procedures' },
      { state: getObservationsState, message: 'observations' },
    ]

    const errorState = errorStates.find(({ state }) => state.error !== false)

    return errorState ? `Unable to retrieve ${errorState.message} from Epic.` : ''
  }

  const isLoading = () => {
    const loadingStates = [
      getPatientState.loading,
      getEncounterState.loading,
      getConditionsState.loading,
      getProceduresState.loading,
      getObservationsState.loading,
      assessmentIsFetching,
      startOver.loading,
      // generatingCarePlan
    ]

    return loadingStates.some((loading) => loading)
  }

  const isError = () => {
    const errorStates = [
      getPatientState.error,
      getEncounterState.error,
      getConditionsState.error,
      getProceduresState.error,
      getObservationsState.error
    ]

    return errorStates.some((error) => error !== false)
  }

  const getLoadingMessage = () => {
    if (assessmentIsFetching) {
      return `Generating assessment for ${patient.firstName} ${patient.middleName || ''} ${patient.lastName}`
    }

    return patientStatus
  }

  return (
    <AGLProvider>
      <IdleTimer
        timeout={30 * 60 * 1000}
        onIdle={handleOnIdle}
        debounce={500}
      />
      <Layout
        showNav={!isLoading() && !isError() && !showEncounterAlert}
        showFooter={!isLoading() && !isError() && !showEncounterAlert}
      >
        <ErrorBoundary>
          {
            isLoading() && <Loader message={getLoadingMessage()} />
          }
          {
            isError() && <ErrorMessage message={renderErrorMessage()} onExit={handleExitClick} onRetry={handleRetryClick} />
          }
          {
            showEncounterAlert && (
              <SessionAlert
                sessionStatus={sessionStatus}
                patient={patient}
                encounter={encounter}
                onResetPatient={handleResetPatient}
                onStartOver={handleStartOverClick}
                onContinue={handleContinueClick}
                onViewSession={handleViewSession}
              />
            )
          }
          {
            !isLoading() && !isError() && !showEncounterAlert && renderStep()
          }
        </ErrorBoundary>
      </Layout>
    </AGLProvider>

  )
}

export default Home
